import CryptoJS from "crypto-js";

class EncryptionService {
    constructor(secretKey) {
        if (!secretKey || secretKey.length === 0) {
            throw new Error("Secret key must be provided");
        }
        this.key = CryptoJS.enc.Utf8.parse(secretKey);
    }

    /**
     * Encrypts a plaintext string using AES (CBC + PKCS7)
     * Returns IV:CipherText (Base64)
     */
    encryptAES(plainText) {
        if (typeof plainText !== "string") {
            throw new Error("Input must be a string");
        }

        // Generate random 16-byte IV
        const iv = CryptoJS.lib.WordArray.random(16);

        // Encrypt
        const encrypted = CryptoJS.AES.encrypt(plainText, this.key, {
            iv: iv,
            mode: CryptoJS.mode.CBC,
            padding: CryptoJS.pad.Pkcs7,
        });

        // Convert IV and cipher to Base64
        const ivBase64 = CryptoJS.enc.Base64.stringify(iv);
        const cipherBase64 = encrypted.ciphertext.toString(CryptoJS.enc.Base64);

        // Join with colon
        return `${ivBase64}:${cipherBase64}`;
    }

    /**
     * Decrypts an IV:CipherText(Base64) string back to plaintext
     */
    decryptAES(encryptedText) {
        if (!encryptedText || !encryptedText.includes(":")) {
            return "Invalid input string";
        }

        const [ivBase64, cipherBase64] = encryptedText.split(":");

        const iv = CryptoJS.enc.Base64.parse(ivBase64);
        const cipherText = CryptoJS.enc.Base64.parse(cipherBase64);

        const decrypted = CryptoJS.AES.decrypt(
            { ciphertext: cipherText },
            this.key,
            {
                iv: iv,
                mode: CryptoJS.mode.CBC,
                padding: CryptoJS.pad.Pkcs7,
            }
        );

        return decrypted.toString(CryptoJS.enc.Utf8);
    }
}

class EncryptionServicePY {
    constructor(key = null) {
        if (key) {
            // Convert string to WordArray and pad/trim to 32 bytes
            const keyUtf8 = CryptoJS.enc.Utf8.parse(key);
            if (keyUtf8.sigBytes < 32) {
                const pad = CryptoJS.lib.WordArray.create(
                    new Uint8Array(32 - keyUtf8.sigBytes)
                );
                this.keyMaterial = keyUtf8.concat(pad);
            } else {
                this.keyMaterial = CryptoJS.lib.WordArray.create(
                    keyUtf8.words.slice(0, 8),
                    32
                );
            }
        } else {
            this.keyMaterial = CryptoJS.lib.WordArray.random(32);
        }
    }

    async encryptAES(input) {
        const iv = CryptoJS.lib.WordArray.random(16);

        const encrypted = CryptoJS.AES.encrypt(input, this.keyMaterial, {
            iv,
            mode: CryptoJS.mode.CBC,
            padding: CryptoJS.pad.Pkcs7,
        });

        const ivBase64 = CryptoJS.enc.Base64.stringify(iv);
        const cipherBase64 = encrypted.ciphertext.toString(CryptoJS.enc.Base64);
        return `${ivBase64}:${cipherBase64}`;
    }

    async decryptAES(encryptedData) {
        try {
            const [ivBase64, cipherBase64] = encryptedData.split(":");

            const iv = CryptoJS.enc.Base64.parse(ivBase64);
            const ciphertext = CryptoJS.enc.Base64.parse(cipherBase64);

            const decrypted = CryptoJS.AES.decrypt(
                { ciphertext },
                this.keyMaterial,
                {
                    iv,
                    mode: CryptoJS.mode.CBC,
                    padding: CryptoJS.pad.Pkcs7,
                }
            );

            return decrypted.toString(CryptoJS.enc.Utf8);
        } catch (error) {
            throw new Error(`Decryption failed: ${error.message}`);
        }
    }
}

export { EncryptionService, EncryptionServicePY };